import SettingsSearchTarget from './SettingsSearchTarget';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import Text from '@/components/Typography/Text';
import TextInput from '@/components/Typography/TextInput';
import Icon from '@/components/Icon';
import { SHORTCUTS } from '@/hooks/useKeyboardShortcuts';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';

const DECK_SHORTCUTS = [
  { action: 'settings.shortcuts.addNewColumn', keys: ['⌘', '⇧', 'N'] },
  { action: 'settings.shortcuts.removeFocusedColumn', keys: ['⌘', '⇧', 'W'] },
  { action: 'settings.shortcuts.focusColumn', keys: ['⌘', '1–9'] },
  { action: 'settings.shortcuts.moveColumnLeft', keys: ['⌘', '⇧', '['] },
  { action: 'settings.shortcuts.moveColumnRight', keys: ['⌘', '⇧', ']'] },
];
const KEY_NAMES = { '⌘': 'command', '⇧': 'shift', '⌥': 'option', Ctrl: 'control', Shift: 'shift', Alt: 'alt', Esc: 'escape', Enter: 'enter' };
const KEY_ALIASES = { '⌘': 'cmd command', '⇧': 'shift', '⌥': 'option opt alt', Ctrl: 'ctrl control', Alt: 'alt option', Esc: 'esc escape', Enter: 'enter return' };
const normalize = value => value.toLocaleLowerCase().replace(/\+/g, ' ').trim();

function Keycaps({ keys, styles }) {
  return <View style={styles.keys} aria-hidden={Platform.OS === 'web' ? true : undefined} accessibilityElementsHidden importantForAccessibility="no-hide-descendants">
    {keys.map((key, index) => <View key={index} style={styles.keycap}>
      <Text size="s" weight="medium" style={styles.keyText}>{key.length === 1 ? key.toUpperCase() : key}</Text>
    </View>)}
  </View>;
}

function ShortcutGroup({ group, styles }) {
  return <View style={styles.group}>
    <View style={styles.groupHeading}>
      <Text size="m" weight="bold" accessibilityRole="header" accessibilityLevel={2}>{group.title}</Text>
      <Text size="s" color="secondary" style={styles.description}>{group.description}</Text>
    </View>
    <View style={styles.card}>
      {group.shortcuts.map((shortcut, index) => <SettingsSearchTarget label={shortcut.title} key={shortcut.action} testID={`shortcut-${shortcut.action}`}
        accessible accessibilityLabel={`${shortcut.title}: ${shortcut.keyNames.join(' + ')}`}
        style={[styles.row, index > 0 && styles.rowDivider]}>
        <Text size="s" style={styles.action}>{shortcut.title}</Text>
        <Keycaps keys={shortcut.displayKeys} styles={styles} />
      </SettingsSearchTarget>)}
    </View>
  </View>;
}

function KeyboardShortcutSettings({ searchTarget } = {}) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  useEffect(() => { setQuery(''); }, [searchTarget]);
  const [focused, setFocused] = useState(false);
  const [width, setWidth] = useState(0);
  const inputRef = useRef(null);
  const styles = useMemo(() => createStyles(colors), [colors]);
  const isMac = Platform.OS === 'macos' || (Platform.OS === 'web' && typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform));
  const displayKeysFor = shortcut => (Platform.OS === 'macos' && shortcut.macKeys ? shortcut.macKeys : shortcut.keys)
    .map(key => !isMac && key === '⌘' ? 'Ctrl' : !isMac && key === '⌥' ? 'Alt' : !isMac && key === '⇧' ? 'Shift' : key);
  const keyName = key => KEY_NAMES[key] ? t(`settings.shortcuts.keys.${KEY_NAMES[key]}`) : key.toUpperCase();
  const entries = Object.entries(SHORTCUTS);
  const groups = [
    { id: 'general', title: t('settings.shortcuts.general'), description: t('settings.shortcuts.generalDescription'),
      shortcuts: [...entries.filter(([id, shortcut]) => !id.startsWith('GO_TO_') && shortcut.group !== 'work').map(([, shortcut]) => shortcut), { action: 'shortcuts.findInChat', keys: ['⌘', 'F'] }] },
    { id: 'navigation', title: t('settings.shortcuts.navigation'), description: t(`settings.shortcuts.${Platform.OS === 'macos' ? 'navigationDescriptionMac' : 'navigationDescription'}`),
      shortcuts: entries.filter(([id]) => id.startsWith('GO_TO_')).map(([, shortcut]) => shortcut) },
    { id: 'work', title: t('settings.shortcuts.work'), description: t('settings.shortcuts.workDescription'),
      shortcuts: entries.filter(([, shortcut]) => shortcut.group === 'work').map(([, shortcut]) => shortcut) },
    // Deck listens to DOM keyboard events; the native Mac app has no equivalent.
    ...(Platform.OS === 'web' ? [{ id: 'deck', title: t('settings.shortcuts.deck'), description: t('settings.shortcuts.deckDescription'), shortcuts: DECK_SHORTCUTS }] : []),
  ].map(group => ({ ...group, shortcuts: group.shortcuts.map(shortcut => {
    const displayKeys = displayKeysFor(shortcut);
    return { ...shortcut, title: t(shortcut.action), displayKeys, keyNames: displayKeys.map(keyName) };
  }) }));
  const terms = normalize(query).split(/\s+/).filter(Boolean);
  const visibleGroups = groups.map(group => ({ ...group, shortcuts: group.shortcuts.filter(shortcut => {
    const text = normalize([group.title, shortcut.title, ...shortcut.displayKeys, ...shortcut.keyNames, ...shortcut.displayKeys.map(key => KEY_ALIASES[key] || '')].join(' '));
    return terms.every(term => text.includes(term));
  }) })).filter(group => group.shortcuts.length);
  const resultCount = visibleGroups.reduce((count, group) => count + group.shortcuts.length, 0);
  const clear = () => { setQuery(''); inputRef.current?.focus(); };
  const legend = isMac ? ['⌘', '⇧', '⌥'] : ['Ctrl', 'Shift', 'Alt'];
  const columns = width >= 680 && !terms.length
    ? [visibleGroups.filter(group => ['general', 'work'].includes(group.id)), visibleGroups.filter(group => ['navigation', 'deck'].includes(group.id))]
    : [visibleGroups];

  return <View testID="keyboard-shortcut-settings" onLayout={event => setWidth(event.nativeEvent.layout.width)}>
    <View style={[styles.search, focused && { borderColor: colors.text_color_primary }]}>
      <Icon name="search" size={15} color={colors.text_color_secondary} />
      <TextInput ref={inputRef} value={query} onChangeText={setQuery}
        accessibilityLabel={t('settings.shortcuts.search')} placeholder={t('settings.shortcuts.search')}
        placeholderTextColor={colors.text_color_secondary} autoCorrect={false} autoCapitalize="none"
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} style={styles.searchInput}
        onKeyPress={event => { if (event.nativeEvent.key === 'Escape' && query) { event.preventDefault?.(); event.stopPropagation?.(); clear(); } }} />
      {!!query && <Pressable accessibilityRole="button" accessibilityLabel={t('settingsPage.clearSearch')} onPress={clear} style={({ hovered, pressed }) => [styles.clear, (hovered || pressed) && { backgroundColor: colors.solid_color_tertiary }]}>
        <Icon name="xmark" size={12} color={colors.text_color_secondary} />
      </Pressable>}
    </View>
    <View style={styles.legend}>
      {legend.map(key => <View key={key} style={styles.legendItem}><Text size="s" weight="medium">{key}</Text><Text size="xs" color="secondary">{keyName(key)}</Text></View>)}
      <Text size="xs" color="secondary" style={styles.legendHint}>{t('settings.shortcuts.pressTogether')}</Text>
    </View>
    {!!terms.length && <Text size="s" color="secondary" accessibilityLiveRegion="polite" style={styles.results}>{t(`settings.shortcuts.${resultCount === 1 ? 'result' : 'results'}`, { count: resultCount })}</Text>}
    <View style={styles.columns}>
      {columns.map((column, index) => <View key={index} style={styles.column}>
        {column.map(group => <ShortcutGroup key={group.id} group={group} styles={styles} />)}
      </View>)}
    </View>
    {!resultCount && <View style={styles.empty}>
      <Icon name="search" size={24} color={colors.text_color_secondary} />
      <Text size="m" weight="bold">{t('settings.shortcuts.noResults')}</Text>
      <Text size="s" color="secondary" style={styles.emptyCopy}>{t('settings.shortcuts.searchHelp')}</Text>
      <Pressable accessibilityRole="button" onPress={clear} style={styles.reset}><Text size="s" weight="medium">{t('settingsPage.clearSearch')}</Text></Pressable>
    </View>}
    {!terms.length && <Text size="xs" color="secondary" style={styles.footer}>{t(`settings.shortcuts.${Platform.OS === 'web' ? 'browserHint' : 'appHint'}`)}</Text>}
  </View>;
}

const createStyles = colors => StyleSheet.create({
  search: { flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 46, paddingLeft: 14, paddingRight: 5, borderWidth: 1, borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block, borderRadius: 12 },
  searchInput: { flex: 1, minWidth: 0, paddingVertical: 13, color: colors.text_color_primary, fontSize: 14, ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}) },
  clear: { width: 36, height: 36, alignItems: 'center', justifyContent: 'center', borderRadius: 8 },
  legend: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 18, paddingVertical: 18, marginBottom: 10 },
  legendItem: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  legendHint: { marginLeft: 'auto' },
  columns: { flexDirection: 'row', gap: 24 },
  column: { flex: 1, minWidth: 0 },
  group: { marginBottom: 28 },
  groupHeading: { gap: 5, marginBottom: 12, paddingHorizontal: 2 },
  description: { lineHeight: 19 },
  card: { borderRadius: 14, borderWidth: 1, borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block, paddingHorizontal: 16 },
  row: { minHeight: 54, flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', columnGap: 12, rowGap: 8, paddingVertical: 11 },
  rowDivider: { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: colors.border_color_secondary },
  action: { flexGrow: 1, flexShrink: 1, flexBasis: 140, lineHeight: 19 },
  keys: { flexDirection: 'row', alignItems: 'center', gap: 5, marginLeft: 'auto' },
  keycap: { minWidth: 29, minHeight: 29, paddingHorizontal: 7, paddingVertical: 4, alignItems: 'center', justifyContent: 'center', borderRadius: 6, borderWidth: 1, borderBottomWidth: 2, borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_primary },
  keyText: { textAlign: 'center' },
  results: { marginBottom: 20 },
  empty: { alignItems: 'center', paddingHorizontal: 24, paddingVertical: 44, gap: 12 },
  emptyCopy: { maxWidth: 320, textAlign: 'center', lineHeight: 20 },
  reset: { minHeight: 44, paddingHorizontal: 16, justifyContent: 'center', borderWidth: 1, borderColor: colors.border_color_secondary, borderRadius: 9, marginTop: 4 },
  footer: { lineHeight: 19, paddingHorizontal: 2 },
});

export default React.memo(KeyboardShortcutSettings);
